import { useCallback, useState, type ReactNode } from 'react';
import { TodayView } from '@ui/phone/today/TodayView';
import { ObjectiveDetailScreen } from '@ui/phone/objective-detail/ObjectiveDetailScreen';
import { NewObjectiveScreen } from '@ui/phone/objective-form/NewObjectiveScreen';
import { EditObjectiveScreen } from '@ui/phone/objective-form/EditObjectiveScreen';
import { RewardsScreen } from '@ui/rewards/RewardsScreen';
import { WeeklyReviewScreen } from '@ui/weekly-review/WeeklyReviewScreen';
import { HabitListScreen } from '@ui/phone/habits/HabitListScreen';
import { HabitFormScreen } from '@ui/phone/habits/HabitFormScreen';
import { ObjectivesScreen } from '@ui/objectives/ObjectivesScreen';
import { CompletionHistoryScreen } from '@ui/history/CompletionHistoryScreen';
import { SettingsScreen } from '@ui/desktop/settings/SettingsScreen';
import { DashboardScreenDesktop } from '@ui/desktop/dashboard/DashboardScreen.desktop';
import { DesktopShell, type DesktopShellDestination } from '@ui/desktop/shell/DesktopShell';
import { ObjectiveFormScreenDesktop } from '@ui/desktop/objective-form/ObjectiveFormScreen.desktop';
import { TodayViewDesktop } from '@ui/desktop/today/TodayView.desktop';
import { HabitsScreenDesktop } from '@ui/desktop/habits/HabitsScreen.desktop';
import { HabitFormScreenDesktop } from '@ui/desktop/habits/HabitFormScreen.desktop';
import { ChevronLeft } from '@ui/shared/icons';
import { useIsDesktopViewport } from '@ui/shared/viewport/useIsDesktopViewport';

/**
 * Composition root's route switch (ADR-0001 rule 5: src/app/ may import
 * everything client-side). No router library needed for a handful of
 * destinations; a Phase 2 desktop surface introduces real routing at that
 * point. 'create-objective' and 'edit-objective' close the "can't do
 * anything after first-run" gap (owner direction): a real "New objective"
 * entry point reachable from Today, and an edit affordance on Objective
 * Detail, both UI-only additions over already-existing domain commands.
 *
 * PHASE 5 (docs/specs/phase-5-objective-creation-rebuild.md,
 * design/screens/objective-create-desktop.md): at desktop viewport,
 * 'create-objective'/'edit-objective' fork to the new full-fidelity
 * `ObjectiveFormScreenDesktop` (category/effort/priority/reminder/cadence
 * goal/status state machine/three-state actions/linking/visibility toggles/
 * live Realism Engine preview) instead of the phone
 * NewObjectiveScreen/EditObjectiveScreen trees — owner-confirmed desktop-only
 * scope; below the breakpoint both routes render the original phone trees
 * completely untouched.
 * 'rewards' is phase-2a-rewards.md's addition: the "How rewards work" +
 * badges + settings screen, reachable from Today's footer "Rewards" link.
 * Both 'rewards' and 'week' import their SWITCH component
 * (`@ui/rewards/RewardsScreen`, `@ui/weekly-review/WeeklyReviewScreen`, per
 * design/screens/responsive-strategy.md) rather than a desktop-only tree
 * directly — each switch picks phone vs. desktop at runtime via
 * useIsDesktopViewport(), fixing the severity-1 bug where a phone visiting
 * either route got the raw desktop tree with zero adaptation.
 * 'habits'/'create-habit'/'edit-habit' are phase-2b-habits.md's addition
 * (HB-15/16/17): originally phone-only, reachable from Today's footer
 * "Habits" link. Now (desktop Today/Habits addition) at desktop viewport
 * these fork to `HabitsScreenDesktop`/`HabitFormScreenDesktop` — genuinely
 * separate desktop trees, with "Habits" promoted to its own `DesktopShell`
 * rail item (mirroring Dashboard's own precedent for promoting a screen
 * into the rail) — mirroring the exact
 * NewObjectiveScreen/EditObjectiveScreen route-switch pattern. Below the
 * breakpoint all three routes render the original phone trees untouched.
 * 'week' is phase-2c-weekly-review.md's addition (WR-23/WR-24): the
 * desktop-optimized Weekly Review screen, reachable via this direct route
 * plus a footer link from the phone Today view.
 *
 * 'dashboard' is the Phase 4 dashboard redesign's addition (owner direct
 * correction, superseding that spec's original "reuse the Objectives
 * screen" call): a genuinely separate desktop destination, first in the
 * rail, and the new desktop default landing route. Phone is entirely
 * unaffected — it still defaults to 'today' and has no dashboard entry
 * point (desktop-reflection-only, matching every other dashboard-adjacent
 * decision).
 *
 * DESKTOP SHELL (phase-2e-nav-settings-export.md NS-1/NS-2, superseding the
 * "no sidebar nav shell exists yet" note above): at desktop viewport
 * (`useIsDesktopViewport()`), 'dashboard'/'today'/'habits'/'week'/
 * 'objectives'/'rewards'/'history'/'settings' all render inside
 * `<DesktopShell>` — the one-off `BackToTodayShell` back-link wrapper this
 * file used to apply to 'rewards'/'week'/'history' is now
 * DESKTOP-shell-only-superseded: below the breakpoint those three routes
 * still render through `BackToTodayShell` exactly as before (no phone tree
 * of their own to govern their own back-navigation chrome), but at/above
 * the breakpoint `DesktopShell`'s rail replaces it. 'objectives' keeps
 * rendering its own phone-tree back/entry affordances unaffected either way
 * (desktop-shell.md §1.2). 'settings' is a desktop-only destination with NO
 * phone entry point at all (not even a `BackToTodayShell`-reachable link) —
 * `SettingsScreen` only ever renders inside `DesktopShell` at desktop
 * viewport (phase-2e §2.1/§5, §7 item 2). `DesktopShell`'s `active` prop is
 * `null` for 'history' (it is a real shelled route but has no rail icon of
 * its own, desktop-shell.md §2).
 *
 * 'today' and 'habits'/'create-habit'/'edit-habit' EACH now have a real,
 * separate desktop tree (`TodayViewDesktop`, `HabitsScreenDesktop`,
 * `HabitFormScreenDesktop`) — superseding the earlier rule that both were
 * phone-only/unshelled-phone-tree destinations at desktop. Below the
 * breakpoint every one of these routes still renders its original,
 * completely untouched phone tree.
 */
type Route =
  /** Desktop dashboard (Phase 4 redesign, owner direct correction): the new
   *  desktop default landing route — a genuinely separate destination from
   *  Objectives, not an addition to it. No phone tree (desktop-only). */
  | { readonly name: 'dashboard' }
  | { readonly name: 'today' }
  | { readonly name: 'objective'; readonly objectiveId: string }
  | { readonly name: 'create-objective' }
  | { readonly name: 'edit-objective'; readonly objectiveId: string }
  | { readonly name: 'rewards' }
  | { readonly name: 'habits' }
  | { readonly name: 'create-habit' }
  | { readonly name: 'edit-habit'; readonly habitId: string }
  | { readonly name: 'week' }
  /** All Objectives overview (phase-2d-management-history.md MC-5/MC-6):
   *  both a phone and a desktop tree, reachable from Today's footer menu. */
  | { readonly name: 'objectives' }
  /** Completion history (phase-2d-management-history.md MC-14/MC-15):
   *  desktop-shaped tree only, reachable from the phone footer for
   *  discoverability (mirrors WR-22's "week" ruling). */
  | { readonly name: 'history' }
  /** Settings (phase-2e-nav-settings-export.md ST-1): desktop-only, reachable
   *  ONLY via `DesktopShell`'s rail — no phone entry point this round. */
  | { readonly name: 'settings' };

export function App() {
  // Drives the back-link wrapper's own phone-vs-desktop padding (see the
  // 'rewards'/'week' route doc comment above) — the same single hook every
  // dual-surface screen switch uses, per responsive-strategy.md §2.1.
  // Read before `route`'s own initial state below so the landing route can
  // depend on it: desktop opens on the Dashboard (phase-4
  // dashboard-redesign.md, owner direct correction — a separate destination
  // from Objectives), phone is unaffected and still lands on Today — this
  // only affects the very first render's default, never overrides
  // subsequent in-session navigation either way.
  const isDesktopViewport = useIsDesktopViewport();
  const [route, setRoute] = useState<Route>(() =>
    isDesktopViewport ? { name: 'dashboard' } : { name: 'today' },
  );

  const openObjective = useCallback((objectiveId: string) => {
    setRoute({ name: 'objective', objectiveId });
  }, []);

  const goToday = useCallback(() => setRoute({ name: 'today' }), []);

  // DesktopShell's onNavigate (NS-3, desktop-shell.md §5): maps a rail
  // destination key onto the existing setRoute mechanism -- no router
  // library introduced. Written as an exhaustive switch (rather than
  // `setRoute({ name: destination })`) so TypeScript can actually verify
  // every DesktopShellDestination maps onto a real zero-argument Route
  // variant -- a plain cast would type-check today but silently stop being
  // checked if either union ever drifts from the other.
  const navigateShell = useCallback((destination: DesktopShellDestination) => {
    switch (destination) {
      case 'dashboard':
        setRoute({ name: 'dashboard' });
        return;
      case 'today':
        setRoute({ name: 'today' });
        return;
      case 'habits':
        setRoute({ name: 'habits' });
        return;
      case 'week':
        setRoute({ name: 'week' });
        return;
      case 'objectives':
        setRoute({ name: 'objectives' });
        return;
      case 'rewards':
        setRoute({ name: 'rewards' });
        return;
      case 'settings':
        setRoute({ name: 'settings' });
        return;
    }
  }, []);

  // Shared by the 'objective', 'edit-objective', and 'create-objective'
  // desktop branches below — all three land on the identical
  // ObjectiveFormScreenDesktop instance (viewing, editing, and creating are
  // one continuous screen at desktop per the owner's "it can't feel
  // different ux" direction), differing only in which objectiveId (or null,
  // for create) is passed in.
  const renderDesktopObjectiveForm = (objectiveId: string | null) => (
    <DesktopShell active="objectives" onNavigate={navigateShell} onOpenObjective={openObjective}>
      {/* Keyed by objectiveId: ObjectiveFormScreenDesktop seeds its local
          draft state (title/description/actions/etc.) from the objectiveId
          prop only on mount via useState/useEffect — without a key,
          navigating between objectives (the "Open" link in Linked
          objectives, or the OBJ N/M switcher) would reuse the same instance
          and leave every field, and every subsequent persistence call,
          silently targeting the PREVIOUS objective. The key forces a clean
          remount per objective, same as swapping a fully different route. */}
      <ObjectiveFormScreenDesktop
        key={objectiveId ?? 'create'}
        objectiveId={objectiveId}
        onDone={() => setRoute({ name: 'objectives' })}
        onDiscard={() => setRoute({ name: 'objectives' })}
        onOpenObjective={(nextId) => setRoute({ name: 'objective', objectiveId: nextId })}
      />
    </DesktopShell>
  );

  if (route.name === 'dashboard') {
    // Desktop-only, no phone entry point (matching 'settings') — if this
    // route is somehow reached below the breakpoint (a resized browser
    // window, not the installed PWA; there is no phone link that sets this
    // route), DesktopShell still renders per its own compression rule
    // rather than a hard redirect.
    return (
      <DesktopShell active="dashboard" onNavigate={navigateShell} onOpenObjective={openObjective}>
        <DashboardScreenDesktop
          onOpenObjective={openObjective}
          onCreateObjective={() => setRoute({ name: 'create-objective' })}
        />
      </DesktopShell>
    );
  }

  if (route.name === 'objective') {
    // Desktop viewport: viewing an objective now routes into the same
    // full-fidelity form/edit screen as 'edit-objective' below — owner
    // direct correction ("use a similar layout from the screen you just
    // created... it can't feel different ux"). There is no separate
    // desktop "detail" screen anymore; the rich form screen already
    // supports live-editing a committed objective, so viewing and editing
    // converge into one screen. Phone is entirely unaffected — it still
    // gets the dedicated ObjectiveDetailScreen with its own Edit entry
    // point into EditObjectiveScreen.
    if (isDesktopViewport) {
      return renderDesktopObjectiveForm(route.objectiveId);
    }
    return (
      <ObjectiveDetailScreen
        objectiveId={route.objectiveId}
        onNavigate={(nextId) => setRoute({ name: 'objective', objectiveId: nextId })}
        onEdit={() => setRoute({ name: 'edit-objective', objectiveId: route.objectiveId })}
        onBack={goToday}
      />
    );
  }

  if (route.name === 'edit-objective') {
    // Phase 5 (docs/specs/phase-5-objective-creation-rebuild.md,
    // design/screens/objective-create-desktop.md): at desktop viewport,
    // editing an objective routes into the new full-fidelity desktop
    // create/edit screen instead of the phone EditObjectiveScreen tree —
    // owner-confirmed scope: the phone tree stays completely untouched and
    // still renders below the breakpoint. Reached both from the 'objective'
    // route above (desktop) and directly (e.g. Weekly Review's "edit"
    // action) — both land on the identical screen at desktop.
    if (isDesktopViewport) {
      return renderDesktopObjectiveForm(route.objectiveId);
    }
    const backToObjective = () => setRoute({ name: 'objective', objectiveId: route.objectiveId });
    return (
      <EditObjectiveScreen
        objectiveId={route.objectiveId}
        onDone={backToObjective}
        onCancel={backToObjective}
        onArchived={goToday}
      />
    );
  }

  if (route.name === 'create-objective') {
    // Same desktop/phone fork as 'edit-objective' above — desktop gets the
    // new full-fidelity draft-first screen (Discard/Save draft/Commit
    // objective), phone keeps its existing single-page NewObjectiveScreen
    // untouched.
    if (isDesktopViewport) {
      return renderDesktopObjectiveForm(null);
    }
    return <NewObjectiveScreen onDone={goToday} onCancel={goToday} />;
  }

  if (route.name === 'rewards') {
    const screen = <RewardsScreen />;
    return isDesktopViewport ? (
      <DesktopShell active="rewards" onNavigate={navigateShell} onOpenObjective={openObjective}>
        {screen}
      </DesktopShell>
    ) : (
      <BackToTodayShell onBack={goToday} isDesktopViewport={isDesktopViewport}>
        {screen}
      </BackToTodayShell>
    );
  }

  if (route.name === 'week') {
    const screen = (
      <WeeklyReviewScreen
        onBack={goToday}
        onEditObjective={(objectiveId) => setRoute({ name: 'edit-objective', objectiveId })}
      />
    );
    return isDesktopViewport ? (
      <DesktopShell active="week" onNavigate={navigateShell} onOpenObjective={openObjective}>
        {screen}
      </DesktopShell>
    ) : (
      <BackToTodayShell onBack={goToday} isDesktopViewport={isDesktopViewport}>
        {screen}
      </BackToTodayShell>
    );
  }

  if (route.name === 'objectives') {
    const screen = (
      <ObjectivesScreen
        onOpenObjective={openObjective}
        onCreateObjective={() => setRoute({ name: 'create-objective' })}
        onBack={goToday}
      />
    );
    return isDesktopViewport ? (
      <DesktopShell active="objectives" onNavigate={navigateShell} onOpenObjective={openObjective}>
        {screen}
      </DesktopShell>
    ) : (
      screen
    );
  }

  if (route.name === 'history') {
    const screen = <CompletionHistoryScreen onOpenObjective={openObjective} />;
    return isDesktopViewport ? (
      // "History" has no rail icon of its own (desktop-shell.md §2) —
      // shelled for chrome/routing purposes only, so no rail item is ever
      // marked active for it.
      <DesktopShell active={null} onNavigate={navigateShell} onOpenObjective={openObjective}>
        {screen}
      </DesktopShell>
    ) : (
      <BackToTodayShell onBack={goToday} isDesktopViewport={isDesktopViewport}>
        {screen}
      </BackToTodayShell>
    );
  }

  if (route.name === 'settings') {
    // Desktop-only, no phone entry point (phase-2e §2.1/§5, §7 item 2) — if
    // this route is somehow reached below the breakpoint (a resized
    // browser window, not the installed PWA; there is no phone link that
    // sets this route), DesktopShell still renders per its own §1
    // compression rule rather than a hard redirect.
    return (
      <DesktopShell active="settings" onNavigate={navigateShell} onOpenObjective={openObjective}>
        <SettingsScreen />
      </DesktopShell>
    );
  }

  if (route.name === 'habits') {
    // Habits is now a genuinely separate desktop destination (own rail
    // item, `src/ui/desktop/habits/HabitsScreen.desktop.tsx`) — the phone
    // tree (`HabitListScreen`) stays completely untouched below the
    // breakpoint.
    if (isDesktopViewport) {
      return (
        <DesktopShell active="habits" onNavigate={navigateShell} onOpenObjective={openObjective}>
          <HabitsScreenDesktop
            onOpenHabit={(habitId) => setRoute({ name: 'edit-habit', habitId })}
            onCreateHabit={() => setRoute({ name: 'create-habit' })}
          />
        </DesktopShell>
      );
    }
    return (
      <HabitListScreen
        onBack={goToday}
        onCreateHabit={() => setRoute({ name: 'create-habit' })}
        onEditHabit={(habitId) => setRoute({ name: 'edit-habit', habitId })}
      />
    );
  }

  if (route.name === 'create-habit') {
    const backToHabits = () => setRoute({ name: 'habits' });
    if (isDesktopViewport) {
      return (
        <DesktopShell active="habits" onNavigate={navigateShell} onOpenObjective={openObjective}>
          <HabitFormScreenDesktop habitId={null} onDone={backToHabits} onCancel={backToHabits} />
        </DesktopShell>
      );
    }
    return <HabitFormScreen habitId={null} onDone={backToHabits} onCancel={backToHabits} />;
  }

  if (route.name === 'edit-habit') {
    const backToHabits = () => setRoute({ name: 'habits' });
    if (isDesktopViewport) {
      return (
        <DesktopShell active="habits" onNavigate={navigateShell} onOpenObjective={openObjective}>
          <HabitFormScreenDesktop habitId={route.habitId} onDone={backToHabits} onCancel={backToHabits} />
        </DesktopShell>
      );
    }
    return <HabitFormScreen habitId={route.habitId} onDone={backToHabits} onCancel={backToHabits} />;
  }

  const todayCallbacks = {
    onOpenObjective: openObjective,
    onCreateObjective: () => setRoute({ name: 'create-objective' }),
    onOpenRewards: () => setRoute({ name: 'rewards' }),
    onOpenHabits: () => setRoute({ name: 'habits' }),
    onOpenWeek: () => setRoute({ name: 'week' }),
    onOpenObjectives: () => setRoute({ name: 'objectives' }),
    onOpenHistory: () => setRoute({ name: 'history' }),
  };

  // Today now has a genuinely separate desktop layout
  // (`src/ui/desktop/today/TodayView.desktop.tsx`) — supersedes the earlier
  // NS-4 rule ("Today does NOT gain a distinct desktop layout"), which
  // applied only until this screen was built. The phone tree stays
  // completely untouched below the breakpoint.
  if (isDesktopViewport) {
    return (
      <DesktopShell active="today" onNavigate={navigateShell} onOpenObjective={openObjective}>
        <TodayViewDesktop {...todayCallbacks} />
      </DesktopShell>
    );
  }
  return <TodayView {...todayCallbacks} />;
}

/**
 * Shared "Back to Today" link + child screen shell for the sub-1024px
 * viewport rendering of routes that have no phone tree of their own
 * (rewards/week/history — see the Route doc comment above). At desktop
 * viewport these three routes render inside `DesktopShell` instead (NS-2);
 * this wrapper is the below-the-breakpoint fallback only.
 */
function BackToTodayShell({
  onBack,
  isDesktopViewport,
  children,
}: {
  readonly onBack: () => void;
  readonly isDesktopViewport: boolean;
  readonly children: ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          padding: `var(--space-4) ${isDesktopViewport ? 'var(--layout-page-margin-desktop)' : 'var(--layout-page-margin-phone)'} 0`,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to Today"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
            marginLeft: '-8px',
          }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Today
        </button>
      </div>
      {children}
    </div>
  );
}
